import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  FolderKanban,
  Sparkles,
  FileText,
  PieChart,
  ShieldAlert,
  Users,
  ArrowRight,
} from "lucide-react";
import { API } from "../../App";

const TYPE_LABELS = {
  transformation: "Transformation",
  creation: "Création",
  innovation: "Innovation",
  restructuration: "Restructuration",
  evenementiel: "Événementiel",
  autre: "Autre",
};

const SOLUTION_PILLARS = [
  {
    icon: Sparkles,
    title: "Cadrage instantané",
    desc: "Décrivez votre idée en 5 étapes, ALIA structure votre projet en quelques minutes.",
  },
  {
    icon: FileText,
    title: "13 livrables générés",
    desc: "Note de cadrage, business plan, SWOT, budget, risques, gouvernance et plus, prêts à exporter.",
  },
  {
    icon: FolderKanban,
    title: "Pilotage opérationnel",
    desc: "Phases, tâches, statuts et priorités pour suivre l'exécution au quotidien.",
  },
  {
    icon: PieChart,
    title: "Indicateurs clés",
    desc: "Avancement, budget consommé, risques ouverts : une vision claire en un coup d'œil.",
  },
];

const OverviewPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API}/projects`, { credentials: "include" });
      if (response.ok) {
        setProjects(await response.json());
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activeProjects = projects.filter((p) => p.status !== "termine");
  const totalBudget = projects.reduce(
    (sum, p) => sum + (p.budget_lines || []).reduce((s, l) => s + (l.amount || 0), 0),
    0
  );
  const openRisks = projects.reduce(
    (sum, p) => sum + (p.risks || []).filter((r) => r.probability === "elevee").length,
    0
  );
  const pendingTasks = projects.reduce(
    (sum, p) => sum + (p.actions || []).filter((a) => a.status === "a_faire").length,
    0
  );

  const kpis = [
    { icon: FolderKanban, label: "Projets actifs", value: activeProjects.length },
    { icon: PieChart, label: "Budget total piloté", value: `${totalBudget.toLocaleString("fr-FR")} €` },
    { icon: ShieldAlert, label: "Risques élevés ouverts", value: openRisks },
    { icon: Users, label: "Actions en attente", value: pendingTasks },
  ];

  return (
    <div className="space-y-8" data-testid="overview-page">
      <Card className="overflow-hidden border-blue-100 dark:border-blue-500/20 bg-gradient-to-br from-blue-50 to-white dark:from-blue-500/10 dark:to-transparent">
        <CardContent className="p-7 sm:p-9">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 dark:bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300">
            ALIA Projets 360°
          </span>
          <h1 className="mt-4 text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
            De l'idée au projet structuré, piloté et exécuté.
          </h1>
          <p className="mt-3 max-w-2xl text-slate-600 dark:text-slate-300 leading-relaxed">
            ALIA accompagne les porteurs de projet à chaque étape : un questionnaire
            de cadrage transforme votre idée en 13 livrables professionnels
            (présentation, business plan, budget, risques...), puis un espace de
            pilotage dédié vous permet de suivre les phases, les tâches et les
            indicateurs clés jusqu'à la clôture.
          </p>
          <Link to="/dashboard/projects/new">
            <Button className="mt-6 bg-blue-600 hover:bg-blue-700 text-white">
              Créer un nouveau projet
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SOLUTION_PILLARS.map((pillar) => {
          const Icon = pillar.icon;
          return (
            <Card key={pillar.title}>
              <CardContent className="p-5">
                <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <p className="mt-3 font-semibold text-slate-900 dark:text-white text-sm" style={{ fontFamily: "Outfit" }}>
                  {pillar.title}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  {pillar.desc}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {projects.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {kpis.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Card key={kpi.label}>
                <CardContent className="p-5">
                  <Icon className="w-5 h-5 text-blue-500" />
                  <p className="mt-2 text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
                    {kpi.value}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{kpi.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
            Vos projets récents
          </h2>
          <Link to="/dashboard/projects" className="text-sm font-medium text-blue-600 dark:text-blue-400">
            Voir tout
          </Link>
        </div>

        {projects.length === 0 ? (
          <Card className="border-dashed border-2 bg-transparent">
            <CardContent className="flex flex-col items-center justify-center py-14 text-center">
              <FolderKanban className="w-10 h-10 text-blue-400 mb-3" />
              <p className="font-semibold text-slate-900 dark:text-white">Aucun projet pour l'instant</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-sm">
                Créez votre premier projet et obtenez vos 13 livrables en quelques minutes.
              </p>
              <Link to="/dashboard/projects/new">
                <Button className="mt-5 bg-blue-600 hover:bg-blue-700 text-white">
                  Créer mon premier projet
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.slice(0, 6).map((project) => (
              <Link key={project.project_id} to={`/dashboard/projects/${project.project_id}`}>
                <Card className="hover:shadow-md transition-all hover:-translate-y-0.5 h-full">
                  <CardContent className="p-5">
                    <Badge className="bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300">
                      {TYPE_LABELS[project.brief?.type] || "Projet"}
                    </Badge>
                    <p className="mt-3 font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>
                      {project.brief?.name || "Projet sans nom"}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                      {project.brief?.sector || "Secteur non précisé"}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewPage;